/**
 * Bumper-car SVG generator — EXACT port of the owner's design component
 * (Car.dc.html): viewBox 0 0 144 204, nose toward −y, rounded rubber ring,
 * closed body, windshield + fixed flat glare, stripes clipped to the body,
 * roof disc (number / flag / motif), optional spoiler, flat shadow.
 *
 * Flat fills only — no gradient, no filter — and ~12 nodes per car: one car is
 * ONE <svg> moved with a transform (DOM screens) or rasterized once into a
 * sprite (canvas renderer, see sprites.ts). The roof ink is black or cream
 * automatically from the disc colour's luminance (shared decalInk) — never a
 * free parameter. Geometry changes here must stay in lockstep with the physics
 * capsule (shared/collision.ts) and the verbatim collision.js module.
 */

import type { CarConfig, CarFlag, CarStripeStyle } from '@microligue/shared';
import { CAR_INK, CAR_VIEW, decalInk, normalizeCarNumber } from '@microligue/shared';

export interface CarSvgOptions {
  /** Flat drop shadow under the car (default true — on-track look). */
  shadow?: boolean;
  /** Rubber ring outline width, viewBox units (component default: 5). */
  outline?: number;
  /**
   * @font-face data URI embedded in the SVG for the roof number. Required for
   * <img>/sprite rasterization (an SVG image cannot fetch external fonts);
   * useless for DOM insertion where the page font applies.
   */
  fontDataUri?: string;
  /** Optional width/height attributes (DOM sizing is otherwise pure CSS). */
  width?: number | string;
  height?: number | string;
}

const CREAM = '#f7f3e8';

let uidCounter = 0;

/** XML-escapes an attribute/text value (configs are validated, this is belt & braces). */
function esc(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const fx = (n: number): string => {
  const r = Math.round(n * 100) / 100;
  return String(Object.is(r, -0) ? 0 : r);
};

// ---------------------------------------------------------------------------
// Stripes (clipped to the body) — every style of the design sheet
// ---------------------------------------------------------------------------

function stripesMarkup(style: CarStripeStyle, s: string): string {
  switch (style) {
    case 'none':
      return '';
    case 'central':
      return `<g fill="${s}"><rect x="56" y="-4" width="9" height="212"/><rect x="79" y="-4" width="9" height="212"/></g>`;
    case 'large':
      return `<rect x="54" y="-4" width="36" height="212" fill="${s}"/>`;
    case 'checker': {
      let sq = '';
      for (let i = 0; i < 13; i++) {
        sq += `<rect x="${56 + (i % 2) * 16}" y="${i * 16}" width="16" height="16"/>`;
      }
      return `<g fill="${s}">${sq}</g>`;
    }
    case 'nose':
      return `<path d="M16 -4 H128 V40 Q72 58 16 40 Z" fill="${s}"/>`;
    case 'half':
      return `<rect x="72" y="-4" width="60" height="212" fill="${s}"/>`;
    case 'flames':
      return (
        `<g fill="${s}">` +
        `<polygon points="16,-4 128,-4 128,92 118,34 106,58 96,30 84,50 72,26 60,50 48,30 38,58 26,34 16,92"/>` +
        `<polygon points="16,208 128,208 128,120 118,172 106,150 96,178 84,156 72,182 60,156 48,178 38,150 26,172 16,120"/>` +
        `</g>`
      );
    case 'chevrons':
      return (
        `<g fill="none" stroke="${s}" stroke-width="12">` +
        `<polyline points="20,98 72,68 124,98"/>` +
        `<polyline points="20,168 72,138 124,168"/>` +
        `</g>`
      );
    case 'dots': {
      const pts: [number, number][] = [
        [46, 34],
        [98, 34],
        [72, 78],
        [40, 112],
        [104, 112],
        [46, 152],
        [98, 152],
      ];
      return `<g fill="${s}">${pts.map(([cx, cy]) => `<circle cx="${cx}" cy="${cy}" r="8"/>`).join('')}</g>`;
    }
    case 'diag': {
      let p = '';
      for (let i = 0; i < 3; i++) {
        p += `<polygon points="16,${56 + i * 52} 128,${8 + i * 52} 128,${28 + i * 52} 16,${76 + i * 52}"/>`;
      }
      return `<g fill="${s}">${p}</g>`;
    }
    case 'sides':
      return `<g fill="${s}"><rect x="16" y="-4" width="13" height="212"/><rect x="115" y="-4" width="13" height="212"/></g>`;
    case 'tail':
      return `<path d="M16 148 Q72 132 128 148 V212 H16 Z" fill="${s}"/>`;
    case 'rays': {
      let l = '';
      for (let i = 0; i < 8; i++) {
        const a = (Math.PI * i) / 4 + Math.PI / 8;
        l +=
          `<line x1="${fx(72 + 27 * Math.cos(a))}" y1="${fx(112 + 27 * Math.sin(a))}"` +
          ` x2="${fx(72 + 110 * Math.cos(a))}" y2="${fx(112 + 110 * Math.sin(a))}"/>`;
      }
      return `<g stroke="${s}" stroke-width="10">${l}</g>`;
    }
    case 'ring':
      return `<circle cx="72" cy="112" r="34" fill="none" stroke="${s}" stroke-width="11"/>`;
    case 'horseshoe':
      return `<path d="M38 73 L38 136 Q38 152 54 152 L90 152 Q106 152 106 136 L106 73" fill="none" stroke="${s}" stroke-width="11"/>`;
    case 'target':
      return (
        `<g fill="none" stroke="${s}">` +
        `<circle cx="72" cy="112" r="31" stroke-width="6"/>` +
        `<circle cx="72" cy="112" r="43" stroke-width="6"/>` +
        `</g>`
      );
    case 'comet':
      return `<polygon points="54,116 90,116 104,208 40,208" fill="${s}"/>`;
    case 'petals': {
      let c = '';
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI * i) / 3;
        c += `<circle cx="${fx(72 + 30 * Math.cos(a))}" cy="${fx(112 + 30 * Math.sin(a))}" r="10"/>`;
      }
      return `<g fill="${s}">${c}</g>`;
    }
    case 'orbit':
      return `<ellipse cx="72" cy="112" rx="48" ry="15" fill="none" stroke="${s}" stroke-width="9" transform="rotate(-30 72 112)"/>`;
    case 'cross':
      return (
        `<g stroke="${s}" stroke-width="14">` +
        `<line x1="24" y1="24" x2="120" y2="184"/>` +
        `<line x1="120" y1="24" x2="24" y2="184"/>` +
        `</g>`
      );
    case 'bars':
      return `<g fill="${s}"><rect x="-4" y="70" width="152" height="14"/><rect x="-4" y="139" width="152" height="14"/></g>`;
  }
}

// ---------------------------------------------------------------------------
// Flags (simplified, clipped to the roof disc) — palette of the design sheet
// ---------------------------------------------------------------------------

function flagMarkup(code: CarFlag): string {
  const x = 54;
  const y = 94;
  const w = 36;
  const h = 36;
  const W = '#f5f4ef';
  const RD = '#e63946';
  const BL = '#1e50a0';
  const GR = '#2f9e5b';
  const YL = '#ffc93c';
  const BK = '#26262e';
  const rect = (rx: number, ry: number, rw: number, rh: number, fill: string): string =>
    `<rect x="${fx(rx)}" y="${fx(ry)}" width="${fx(rw)}" height="${fx(rh)}" fill="${fill}"/>`;
  const v = (a: string, b: string, c: string): string =>
    rect(x, y, w / 3, h, a) +
    rect(x + w / 3, y, w / 3, h, b) +
    rect(x + (2 * w) / 3, y, w / 3, h, c);
  const hz = (a: string, b: string, c: string): string =>
    rect(x, y, w, h / 3, a) +
    rect(x, y + h / 3, w, h / 3, b) +
    rect(x, y + (2 * h) / 3, w, h / 3, c);
  switch (code) {
    case 'fr':
      return v(BL, W, RD);
    case 'it':
      return v(GR, W, RD);
    case 'de':
      return hz(BK, RD, YL);
    case 'es':
      return rect(x, y, w, h, RD) + rect(x, y + 9, w, 18, YL);
    case 'jp':
      return rect(x, y, w, h, W) + `<circle cx="72" cy="112" r="8" fill="${RD}"/>`;
    case 'gb':
      return (
        rect(x, y, w, h, BL) +
        rect(x, y + 12.5, w, 11, W) +
        rect(66.5, y, 11, h, W) +
        rect(x, y + 15, w, 6, RD) +
        rect(69, y, 6, h, RD)
      );
    case 'br':
      return (
        rect(x, y, w, h, GR) +
        `<polygon points="72,96 88,112 72,128 56,112" fill="${YL}"/>` +
        `<circle cx="72" cy="112" r="7" fill="${BL}"/>`
      );
    case 'us':
      return (
        rect(x, y, w, h, W) +
        rect(x, y, w, 4.5, RD) +
        rect(x, y + 9, w, 4.5, RD) +
        rect(x, y + 18, w, 4.5, RD) +
        rect(x, y + 27, w, 4.5, RD) +
        rect(x, y, 17, 16, BL)
      );
  }
}

// ---------------------------------------------------------------------------
// Roof disc (number / flag / motif)
// ---------------------------------------------------------------------------

function decalMarkup(cfg: CarConfig, uid: string): string {
  if (cfg.decal === 'none') return '';
  const pCol = cfg.decalColor;
  const ink = decalInk(pCol);
  const disc = (fill: string): string =>
    `<circle cx="72" cy="112" r="22" fill="${esc(fill)}" stroke="${CAR_INK}" stroke-width="4"/>`;

  if (cfg.decal === 'number') {
    const num = normalizeCarNumber(cfg.number);
    const text = num
      ? `<text x="72" y="119.5" text-anchor="middle" font-family="'Bungee',sans-serif" font-size="20" fill="${ink}">${esc(num)}</text>`
      : '';
    return `<g>${disc(pCol)}${text}</g>`;
  }
  if (cfg.decal === 'flag') {
    // Flag discs keep the cream background of the design sheet.
    return (
      `<g>${disc(CREAM)}` +
      `<clipPath id="${uid}f"><circle cx="72" cy="112" r="17"/></clipPath>` +
      `<g clip-path="url(#${uid}f)">${flagMarkup(cfg.flag)}</g>` +
      `<circle cx="72" cy="112" r="17" fill="none" stroke="${CAR_INK}" stroke-width="2"/>` +
      `</g>`
    );
  }

  let motif = '';
  switch (cfg.decal) {
    case 'star':
      motif = `<polygon points="72,96 76.4,106.5 87.8,107.4 79.1,114.9 81.7,126 72,120.1 62.3,126 64.9,114.9 56.2,107.4 67.6,106.5" fill="${ink}"/>`;
      break;
    case 'bolt':
      motif = `<polygon points="79,94 58,118 70,118 65,132 86,108 74,108" fill="${ink}"/>`;
      break;
    case 'crown':
      motif = `<polygon points="57,124 57,104 65,113 72,97 79,113 87,104 87,124" fill="${ink}"/>`;
      break;
    case 'heart':
      motif = `<path d="M72 128 C53 114 57 95 72 105 C87 95 91 114 72 128 Z" fill="${ink}"/>`;
      break;
    case 'diamond':
      motif = `<polygon points="72,95 87,112 72,129 57,112" fill="${ink}"/>`;
      break;
    case 'clover':
      motif =
        `<g fill="${ink}">` +
        `<circle cx="72" cy="103" r="6"/><circle cx="64" cy="114" r="6"/><circle cx="80" cy="114" r="6"/>` +
        `<polygon points="70,116 74,116 77,128 67,128"/>` +
        `</g>`;
      break;
    case 'moon':
      motif = `<path d="M79 96 A16.5 16.5 0 1 0 79 128 A13 13 0 1 1 79 96 Z" fill="${ink}"/>`;
      break;
    case 'spade':
      motif = `<path d="M72 95 C86 109 84 121 73 117 L77 128 H67 L71 117 C60 121 58 109 72 95 Z" fill="${ink}"/>`;
      break;
    case 'arrow':
      motif = `<polygon points="72,94 86,110 77,110 77,128 67,128 67,110 58,110" fill="${ink}"/>`;
      break;
    case 'shield':
      motif = `<path d="M72 95 L87 100 V112 C87 121 81 127 72 130 C63 127 57 121 57 112 V100 Z" fill="${ink}"/>`;
      break;
    case 'skull':
      motif =
        `<circle cx="72" cy="108" r="10" fill="${ink}"/>` +
        `<rect x="66" y="114" width="12" height="10" rx="3" fill="${ink}"/>` +
        `<circle cx="68" cy="107" r="3" fill="${esc(pCol)}"/>` +
        `<circle cx="76" cy="107" r="3" fill="${esc(pCol)}"/>`;
      break;
    case 'paw':
      motif =
        `<g fill="${ink}">` +
        `<ellipse cx="72" cy="118" rx="8" ry="6.5"/>` +
        `<circle cx="61" cy="108" r="4"/><circle cx="72" cy="104" r="4"/><circle cx="83" cy="108" r="4"/>` +
        `</g>`;
      break;
    case 'dot':
      motif = `<circle cx="72" cy="112" r="9" fill="${ink}"/>`;
      break;
  }
  return `<g>${disc(pCol)}${motif}</g>`;
}

// ---------------------------------------------------------------------------
// The car
// ---------------------------------------------------------------------------

/** The complete car as an SVG string (see CarSvgOptions for the raster knobs). */
export function carSvgString(cfg: CarConfig, opts: CarSvgOptions = {}): string {
  const uid = `car${++uidCounter}`;
  const shadow = opts.shadow ?? true;
  const outline = opts.outline ?? 5;
  const sizeAttrs =
    (opts.width !== undefined ? ` width="${esc(String(opts.width))}"` : '') +
    (opts.height !== undefined ? ` height="${esc(String(opts.height))}"` : '');
  const body = esc(cfg.body);
  const rubber = esc(cfg.rubber);
  const glass = esc(cfg.glass);
  const stripes = stripesMarkup(cfg.stripeStyle, esc(cfg.stripe));

  const font = opts.fontDataUri
    ? `<style>@font-face{font-family:'Bungee';src:url(${opts.fontDataUri}) format('woff2')}</style>`
    : '';

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CAR_VIEW.w} ${CAR_VIEW.h}"${sizeAttrs}>` +
    font +
    (shadow ? `<ellipse cx="76" cy="106" rx="66" ry="96" fill="rgba(24,26,40,0.14)"/>` : '') +
    `<rect x="4" y="4" width="136" height="196" rx="62" fill="${rubber}" stroke="${CAR_INK}" stroke-width="${fx(outline)}"/>` +
    `<rect x="16" y="16" width="112" height="172" rx="52" fill="${body}" stroke="${CAR_INK}" stroke-width="4"/>` +
    `<clipPath id="${uid}"><rect x="16" y="16" width="112" height="172" rx="52"/></clipPath>` +
    (stripes ? `<g clip-path="url(#${uid})">${stripes}</g>` : '') +
    `<path d="M40 44 Q72 33 104 44 L99 67 Q72 58 45 67 Z" fill="${glass}" stroke="${CAR_INK}" stroke-width="4" stroke-linejoin="round"/>` +
    `<path d="M50 42 L58 40 L53 62 L48 60 Z" fill="rgba(255,255,255,0.25)"/>` +
    decalMarkup(cfg, uid) +
    (cfg.spoiler
      ? `<rect x="50" y="158" width="8" height="14" fill="${CAR_INK}"/>` +
        `<rect x="86" y="158" width="8" height="14" fill="${CAR_INK}"/>` +
        `<rect x="34" y="166" width="76" height="15" rx="7" fill="${esc(cfg.spoilerColor)}" stroke="${CAR_INK}" stroke-width="4"/>`
      : '') +
    `</svg>`
  );
}

/** The car as a live DOM node (configurator preview, list swatches…). */
export function createCar(cfg: CarConfig, opts: CarSvgOptions = {}): SVGSVGElement {
  const tpl = document.createElement('template');
  tpl.innerHTML = carSvgString(cfg, opts);
  return tpl.content.firstElementChild as SVGSVGElement;
}

/**
 * Small inline car for lists (waiting room, results, wardrobe). The nose
 * points up; height drives the size (ratio 144:204).
 */
export function carSwatch(cfg: CarConfig, heightPx = 34): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'car-swatch';
  const w = Math.round((heightPx * CAR_VIEW.w) / CAR_VIEW.h);
  try {
    const svg = createCar(cfg, { shadow: false, outline: 6 });
    svg.style.height = `${heightPx}px`;
    svg.style.width = `${w}px`;
    span.appendChild(svg);
  } catch {
    // Minimal/fake DOM (tests, exotic embeds): a body-coloured pill instead.
    span.className = 'car-swatch car-swatch-fallback';
    span.style.background = cfg.body;
    span.style.width = `${w}px`;
    span.style.height = `${heightPx}px`;
  }
  return span;
}
